import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Music, Play, Pause, Upload, Trash2,
  Check, Loader2, Volume2, Radio
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const HoldMusic = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/hold-music/settings`, { headers })
      .then(r => { setSettings(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const togglePlay = () => {
    if (!audioRef.current || !settings?.audio_url) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.src = settings.audio_url;
      audioRef.current.play().catch(() => {});
      setPlaying(true);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(mp3|wav)$/i)) {
      toast({ title: 'Only MP3 and WAV files are supported', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File must be under 5MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/hold-music/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Custom hold music uploaded' });
      const res = await axios.get(`${API}/hold-music/settings`, { headers });
      setSettings(res.data);
      setPlaying(false);
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const switchToDefault = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/hold-music/settings`, { use_default: true, custom_filename: '' }, { headers });
      toast({ title: 'Switched to default hold music' });
      const res = await axios.get(`${API}/hold-music/settings`, { headers });
      setSettings(res.data);
      setPlaying(false);
    } catch {
      toast({ title: 'Failed to update', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const deleteCustom = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/hold-music/custom`, { headers });
      toast({ title: 'Custom hold music removed' });
      const res = await axios.get(`${API}/hold-music/settings`, { headers });
      setSettings(res.data);
      setPlaying(false);
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0a1628] flex items-center justify-center"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="hold-music-page">
      <audio ref={audioRef} onEnded={() => setPlaying(false)} />
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold" data-testid="hold-music-title">Hold Music</h1>
            <p className="text-gray-500 text-sm">Customize what callers hear during transfers</p>
          </div>
        </div>

        {/* Current Hold Music */}
        <Card className="bg-white/[0.03] border-white/[0.06] mb-6" data-testid="current-music-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-purple-400" /> Active Hold Music
            </CardTitle>
            <CardDescription className="text-gray-400">
              This plays when callers are transferred or put on hold
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/20">
              <button
                onClick={togglePlay}
                className="w-14 h-14 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center transition-colors flex-shrink-0 shadow-lg shadow-purple-500/25"
                data-testid="play-btn"
              >
                {playing ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">
                  {settings?.use_default ? 'Default Hold Music' : 'Custom Hold Music'}
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {settings?.use_default
                    ? 'Miadesk — Calm piano melody'
                    : settings?.original_name || settings?.custom_filename || 'Custom upload'}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${settings?.use_default ? 'bg-blue-500/15 text-blue-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                    {settings?.use_default ? 'Default' : 'Custom'}
                  </span>
                  <span className="text-[10px] text-gray-600">MP3</span>
                </div>
              </div>
              {/* Visualizer bars */}
              <div className="flex items-end gap-0.5 h-8 flex-shrink-0">
                {[3, 5, 4, 6, 3, 5, 4, 6, 3, 5].map((h, i) => (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-all duration-300 ${playing ? 'bg-purple-400' : 'bg-gray-700'}`}
                    style={{
                      height: playing ? `${h * 4 + Math.sin(Date.now() / 200 + i) * 4}px` : `${h * 2}px`,
                      animationDelay: `${i * 100}ms`,
                    }}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Custom */}
        <Card className="bg-white/[0.03] border-white/[0.06] mb-6" data-testid="upload-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-400" /> Upload Custom Music
            </CardTitle>
            <CardDescription className="text-gray-400">
              Upload your own hold music (MP3 or WAV, max 5MB)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input type="file" ref={fileInputRef} accept=".mp3,.wav" onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-white/10 hover:border-purple-500/30 rounded-xl p-8 text-center transition-colors group"
              data-testid="upload-area"
            >
              {uploading ? (
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
              ) : (
                <Music className="w-8 h-8 text-gray-600 group-hover:text-purple-400 mx-auto mb-2 transition-colors" />
              )}
              <p className="text-sm text-gray-400 group-hover:text-gray-300">
                {uploading ? 'Uploading...' : 'Click to upload MP3 or WAV file'}
              </p>
              <p className="text-xs text-gray-600 mt-1">Max 5MB</p>
            </button>
          </CardContent>
        </Card>

        {/* Switch Options */}
        <Card className="bg-white/[0.03] border-white/[0.06]" data-testid="options-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Radio className="w-5 h-5 text-emerald-400" /> Music Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Default option */}
            <button
              onClick={switchToDefault}
              disabled={saving || settings?.use_default}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                settings?.use_default
                  ? 'bg-purple-600/10 border-purple-500/30'
                  : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]'
              }`}
              data-testid="option-default"
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                settings?.use_default ? 'border-purple-400 bg-purple-400' : 'border-gray-600'
              }`}>
                {settings?.use_default && <Check className="w-3 h-3 text-white" />}
              </div>
              <div>
                <p className="text-sm font-medium text-white">Default Hold Music</p>
                <p className="text-xs text-gray-500">Miadesk calm piano melody — included free</p>
              </div>
            </button>

            {/* Custom option */}
            {settings?.has_custom && (
              <div className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                !settings?.use_default
                  ? 'bg-purple-600/10 border-purple-500/30'
                  : 'bg-white/[0.02] border-white/[0.06]'
              }`}>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  !settings?.use_default ? 'border-purple-400 bg-purple-400' : 'border-gray-600'
                }`}>
                  {!settings?.use_default && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Custom Upload</p>
                  <p className="text-xs text-gray-500">{settings?.original_name || settings?.custom_filename}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={deleteCustom}
                  disabled={saving}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="delete-custom-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Hold music info */}
            <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/20 mt-4" data-testid="hold-music-info">
              <p className="text-sm font-medium text-white">When does hold music play?</p>
              <p className="text-xs text-gray-500 mt-0.5">Callers hear this music while on hold — for example during a transfer to a team member. It does not play while Mia is talking.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HoldMusic;
